import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Video, VideoOff, Mic, ShieldAlert, Download, Circle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ADMIN_CONTACT = "4055068920";
const REASONS = [
  "Passenger requested privacy",
  "Sensitive conversation",
  "Minor passenger onboard",
  "Medical / health situation",
  "Law enforcement / safety",
  "Other (see note)",
];

export default function TripRecorder({ tripId, autoStart = true, compact = false }) {
  const [status, setStatus] = useState("idle"); // idle | recording | paused-privacy
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [breakOpen, setBreakOpen] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [note, setNote] = useState("");
  const mediaRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobUrlRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (autoStart) start();
    return () => stop(true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (status === "recording") {
      tickRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      clearInterval(tickRef.current);
    }
    return () => clearInterval(tickRef.current);
  }, [status]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: { facingMode: "user" } });
      mediaRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = URL.createObjectURL(blob);
      };
      rec.start(1000);
      recorderRef.current = rec;
      setStatus("recording"); setError(null);
      toast.success("Trip recording started (audio + video)");
    } catch (e) {
      setError(e.message || "Recording permission denied");
      toast.error("Recording unavailable — check camera/mic permissions");
    }
  };

  const stop = (silent = false) => {
    try { recorderRef.current?.stop(); } catch {}
    mediaRef.current?.getTracks().forEach((t) => t.stop());
    recorderRef.current = null; mediaRef.current = null;
    setStatus("idle");
    if (!silent) toast.success("Recording stopped");
  };

  const download = () => {
    if (!blobUrlRef.current) return toast.error("Nothing recorded yet");
    const a = document.createElement("a");
    a.href = blobUrlRef.current;
    a.download = `trip-${tripId || Date.now()}.webm`;
    a.click();
  };

  const engagePrivacy = async () => {
    setBreakOpen(false);
    // stop tracks immediately
    mediaRef.current?.getAudioTracks().forEach((t) => (t.enabled = false));
    mediaRef.current?.getVideoTracks().forEach((t) => (t.enabled = false));
    setStatus("paused-privacy");

    // get GPS + reverse geocode
    let lat = null, lng = null, address = null;
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }));
      lat = pos.coords.latitude; lng = pos.coords.longitude;
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (apiKey) {
        try {
          const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`);
          const j = await r.json();
          address = j.results?.[0]?.formatted_address || null;
        } catch {}
      }
    } catch {}

    const fullReason = note ? `${reason} — ${note}` : reason;

    // Log to backend (audit trail)
    try {
      await api.post("/privacy/alert", { reason: fullReason, trip_id: tripId, gps_lat: lat, gps_lng: lng, gps_address: address });
    } catch {}

    // Also open native SMS to admin for immediate visibility (until Twilio integrated)
    const msg = `Buller Taxi — Privacy activated\nDriver paused recording.\nReason: ${fullReason}\nLocation: ${address || (lat && lng ? `${lat.toFixed(5)},${lng.toFixed(5)}` : "unknown")}\nTime: ${new Date().toLocaleString()}`;
    const sep = navigator.userAgent.match(/(iPhone|iPad|iPod|Mac OS)/) ? "&" : "?";
    try { window.open(`sms:${ADMIN_CONTACT}${sep}body=${encodeURIComponent(msg)}`, "_blank"); } catch {}
    toast.warning("Privacy engaged — admin notified for transparency");
  };

  const resumeFromPrivacy = () => {
    mediaRef.current?.getAudioTracks().forEach((t) => (t.enabled = true));
    mediaRef.current?.getVideoTracks().forEach((t) => (t.enabled = true));
    setStatus("recording");
    toast.success("Recording resumed");
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  const indicator = (
    <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest">
      {status === "recording" && (<><span className="h-2 w-2 rounded-full bg-red-500 pulse-dot"/><span className="text-red-400">REC {mm}:{ss}</span></>)}
      {status === "paused-privacy" && (<><span className="h-2 w-2 rounded-full bg-amber-400"/><span className="text-amber-400">PRIVACY</span></>)}
      {status === "idle" && (<><span className="h-2 w-2 rounded-full bg-zinc-600"/><span className="text-zinc-500">OFF</span></>)}
    </span>
  );

  return (
    <div className={compact ? "flex items-center gap-2" : "flex items-center gap-2 flex-wrap"} data-testid="trip-recorder">
      {indicator}
      {status === "idle" && (
        <Button data-testid="rec-start-btn" size="sm" onClick={start} className="h-8 bg-red-600 hover:bg-red-500 text-white text-xs font-bold">
          <Circle className="h-3 w-3 mr-1 fill-current"/>REC
        </Button>
      )}
      {status === "recording" && (
        <>
          <Dialog open={breakOpen} onOpenChange={setBreakOpen}>
            <DialogTrigger asChild>
              <Button data-testid="rec-privacy-btn" size="sm" variant="outline" className="h-8 bg-amber-400/10 border-amber-400/50 text-amber-400 text-xs font-bold hover:bg-amber-400/20">
                <ShieldAlert className="h-3 w-3 mr-1"/>PRIVACY
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-amber-400/40 text-zinc-100" data-testid="rec-privacy-dialog">
              <DialogHeader>
                <DialogTitle className="text-amber-400 flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5"/>Break-Glass Privacy
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs text-zinc-400">
                Engaging privacy pauses audio + video recording. For transparency, the admin at <span className="text-amber-400 font-bold">405-506-8920</span> will be notified with your reason, GPS location, and timestamp.
              </p>
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider">Reason</label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="privacy-reason-select" className="bg-zinc-950 border-zinc-700 mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                    {REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-zinc-400 uppercase tracking-wider">Additional note (optional)</label>
                <Textarea data-testid="privacy-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="bg-zinc-950 border-zinc-700 mt-1" rows={3}/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="privacy-cancel-btn" variant="outline" onClick={() => setBreakOpen(false)} className="bg-zinc-950 border-zinc-700">Cancel</Button>
                <Button data-testid="privacy-confirm-btn" onClick={engagePrivacy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">ENGAGE PRIVACY</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button data-testid="rec-stop-btn" size="sm" variant="outline" onClick={() => stop(false)} className="h-8 bg-zinc-950 border-zinc-700 text-xs">
            <VideoOff className="h-3 w-3 mr-1"/>STOP
          </Button>
        </>
      )}
      {status === "paused-privacy" && (
        <Button data-testid="rec-resume-btn" size="sm" onClick={resumeFromPrivacy} className="h-8 bg-red-600 hover:bg-red-500 text-white text-xs font-bold">
          <Video className="h-3 w-3 mr-1"/>RESUME REC
        </Button>
      )}
      {blobUrlRef.current && status !== "recording" && (
        <Button data-testid="rec-download-btn" size="sm" variant="outline" onClick={download} className="h-8 bg-zinc-950 border-zinc-700 text-xs">
          <Download className="h-3 w-3 mr-1"/>SAVE
        </Button>
      )}
      {error && <span className="text-[10px] text-red-400">{error}</span>}
    </div>
  );
}
